import { t } from '@trimble/i18n';
import { JSX } from 'solid-js';
import { Icon, SwitchButton } from '@tcweb/connect-ui';
import type { MeasurementsCapabilityProvider } from '@trimble/web3d-providers';
import type { PointMeasurementController } from './point-measurement.types';

export interface PointMeasurementOptionsProps {
  controller?: PointMeasurementController;
  measurementsProvider?: MeasurementsCapabilityProvider;
  showXYCoordinates?: boolean;
  showZCoordinate?: boolean;
  onShowXYCoordinatesChange?(checked: boolean): void;
  onShowZCoordinateChange?(checked: boolean): void;
  disableEdit?: boolean;
  disabledReason?: string;
}

export const PointMeasurementOptions = (
  props: PointMeasurementOptionsProps
): JSX.Element => {
  const getShowXYCoordinates = (): boolean => {
    if (props.controller) {
      return props.controller.showXYCoordinates();
    }
    return (
      props.showXYCoordinates ??
      props.measurementsProvider?.pointMeasurementSettings()
        .showXYCoordinates ??
      false
    );
  };

  const getShowZCoordinate = (): boolean => {
    if (props.controller) {
      return props.controller.showZCoordinate();
    }
    return (
      props.showZCoordinate ??
      props.measurementsProvider?.pointMeasurementSettings().showZCoordinate ??
      false
    );
  };

  const handleShowXYCoordinatesChange = (checked: boolean): void => {
    if (props.controller) {
      props.controller.setShowXYCoordinates(checked);
    } else if (props.onShowXYCoordinatesChange) {
      props.onShowXYCoordinatesChange(checked);
    } else if (props.measurementsProvider) {
      props.measurementsProvider.setPointMeasurementSettings({
        ...props.measurementsProvider.pointMeasurementSettings(),
        showXYCoordinates: checked,
      });
    }
  };

  const handleShowZCoordinateChange = (checked: boolean): void => {
    if (props.controller) {
      props.controller.setShowZCoordinate(checked);
    } else if (props.onShowZCoordinateChange) {
      props.onShowZCoordinateChange(checked);
    } else if (props.measurementsProvider) {
      props.measurementsProvider.setPointMeasurementSettings({
        ...props.measurementsProvider.pointMeasurementSettings(),
        showZCoordinate: checked,
      });
    }
  };

  return (
    <div>
      <div class="flex items-center gap-2 px-4">
        <Icon icon="svg-xy" size="icon-medium" />
        <span class="flex-1 text-gray-8">
          {t('measurements.point.XYCoordinate')}
        </span>
        <modus-wc-tooltip
          content={props.disabledReason}
          position="bottom"
          disabled={!props.disableEdit}
        >
          <SwitchButton
            name="xy-coordinate"
            isChecked={getShowXYCoordinates()}
            onChange={handleShowXYCoordinatesChange}
            disabled={props.disableEdit}
          />
        </modus-wc-tooltip>
      </div>
      <div class="flex items-center gap-2 px-4">
        <Icon icon="svg-z" size="icon-medium" />
        <span class="flex-1 text-gray-8">
          {t('measurements.point.ZCoordinate')}
        </span>
        <modus-wc-tooltip
          content={props.disabledReason}
          position="bottom"
          disabled={!props.disableEdit}
        >
          <SwitchButton
            name="z-coordinate"
            isChecked={getShowZCoordinate()}
            onChange={handleShowZCoordinateChange}
            disabled={props.disableEdit}
          />
        </modus-wc-tooltip>
      </div>
    </div>
  );
};
